// ---------------------------------------------------------------------------
// YouAreYou (uru) — Icon Component
//
// Shared wrapper for Material Symbols Outlined icons.
// Provides a clean API for rendering icons with optional fill,
// custom weight, size, and className overrides.
//
// Usage:
//   <Icon name="verified_user" />
//   <Icon name="check" filled />
//   <Icon name="settings" className="text-2xl text-primary-500" />
// ---------------------------------------------------------------------------

interface IconProps {
  /** Material Symbols icon name (e.g., "verified_user", "check_circle"). */
  name: string;

  /**
   * Whether to use the filled variant of the icon.
   * Sets font-variation-settings FILL to 1.
   * @default false
   */
  filled?: boolean;

  /** Additional CSS classes to apply. */
  className?: string;

  /**
   * Inline styles. Use `fontSize` to reliably override Material Symbols'
   * unlayered 24px default, which beats all Tailwind utility classes.
   */
  style?: React.CSSProperties;

  /**
   * Accessible label for the icon. If provided, sets aria-label.
   * If omitted, the icon is treated as decorative (aria-hidden="true").
   */
  label?: string;
}

export default function Icon({
  name,
  filled = false,
  className = "",
  style,
  label,
}: IconProps) {
  const inlineStyle: React.CSSProperties = {
    ...(filled ? { fontVariationSettings: "'FILL' 1" } : {}),
    ...style,
  };
  return (
    <span
      className={`material-symbols-outlined select-none ${className}`}
      style={Object.keys(inlineStyle).length ? inlineStyle : undefined}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
    >
      {name}
    </span>
  );
}
